import { RechazoMatrizCollapse } from '@/components/asesorias/RechazoMatrizCollapse';
import { HistorialAnteriorMatrizCollapse } from '@/components/asesorias/HistorialAnteriorMatrizCollapse';
import { Input, Textarea } from '@/components/ui';
import type { RequisitoConAsesoria, ComentarioAnterior } from '@/services/requisitos.service';
import { useEvaluacionStore } from '@/stores/useEvaluacionStore';
import { useEvaluacionTernaStore, useTernaEvalValues } from '@/stores/useEvaluacionTernaStore';
import type { CalificacionType, CumplimientoNTC } from '@/types/types';
import { renderWithBold } from '@/utils/renderWithBold';
import { estaRechazadoVigente, tieneRechazoActivo, type RevisionEntry } from '@/utils/revisionHistorial';
import { CheckCircle, Info, Lock, RotateCcw, XCircle } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

/** Rechazos (hilo de historial) por campo de una terna, para mostrarlos inline en la matriz. */
export interface RechazosTernaCampos {
  observaciones?: RevisionEntry[];
  planMejora?: RevisionEntry[];
  fechaImplementacion?: RevisionEntry[];
}

interface Evidencia {
  evidenciaId: number;
  nombre: string;
  descripcion: string | null;
  fechaAdicion: Date;
  url: string;
  tipoArchivo: string | null;
  estado: 'ACTIVO' | 'PENDIENTE' | 'ELIMINADO' | 'APROBADO' | 'RECHAZADO';
  asesoriaId: number;
  observacion?: string | null;
  reemplazaEvidenciaId?: number | null;
}

/** Columna de NTC derivada de combinacion.ntcs o de CumplimientoNTC */
export interface ColumnaNtc {
  ntcId: number;
  nombre: string;
  orden: number;
}

/** Una fila de la matriz: cada NTC (por orden) tiene 0 o 1 requisito */
interface FilaEvaluacion {
  id: string;
  ternaId: number | null;
  grupo: string | null;
  celdas: Record<number, RequisitoConAsesoria | null>; // orden → req or null
}

/** Grupo visual de filas que comparten al menos un requisito */
export interface GrupoEvaluacion {
  id: string;
  filas: FilaEvaluacion[];
}

interface NumeralKey {
  isAnnex: boolean;
  annexLetter: string;
  parts: number[];
}

function parseNumeral(numeral?: string): NumeralKey {
  if (!numeral) return { isAnnex: false, annexLetter: '', parts: [Infinity] };
  const trimmed = numeral.trim();
  let isAnnex = false;
  let annexLetter = '';
  let rest = trimmed;

  const anexoMatch = trimmed.match(/^Anexo\s+([A-Za-z])/i);
  if (anexoMatch) {
    isAnnex = true;
    annexLetter = anexoMatch[1].toUpperCase();
    rest = trimmed.slice(anexoMatch[0].length).replace(/^\.?\s*/, '');
  } else if (/^[A-Za-z][.\s]/.test(trimmed)) {
    isAnnex = true;
    annexLetter = trimmed[0].toUpperCase();
    rest = trimmed.slice(1).replace(/^\.?\s*/, '');
  }

  const numMatch = rest.match(/^\d+(?:\.\d+)*/);
  const parts: number[] = [];
  if (numMatch) {
    for (const p of numMatch[0].split('.')) {
      const n = parseInt(p, 10);
      if (!isNaN(n)) parts.push(n);
    }
  }
  if (parts.length === 0) parts.push(isAnnex ? 0 : Infinity);

  return { isAnnex, annexLetter, parts };
}

function compareNumerals(a: NumeralKey, b: NumeralKey): number {
  if (a.isAnnex !== b.isAnnex) return a.isAnnex ? 1 : -1;
  if (a.annexLetter !== b.annexLetter) return a.annexLetter.localeCompare(b.annexLetter);
  const maxLen = Math.max(a.parts.length, b.parts.length);
  for (let i = 0; i < maxLen; i++) {
    const ap = a.parts[i] ?? -1;
    const bp = b.parts[i] ?? -1;
    if (ap !== bp) return ap - bp;
  }
  return 0;
}

// Componente para celda de requisitos (con calificación per-requisito)
export const RequisitosCell = React.memo<{
  req: RequisitoConAsesoria;
  expandAll?: boolean;
  disabled?: boolean;
  asesoriaLabelFn?: (id: number) => string;
  /** En modo correcciones, solo los elementos rechazados quedan editables. */
  soloEditablesRechazados?: boolean;
  revisionHistorial?: any[];
  keysRechazadasRondaActual?: Set<string>;
  rondaActual?: number;
  comentariosAnteriores?: ComentarioAnterior[];
  historialAnterior?: any[];
}>(
  ({
    req,
    expandAll,
    disabled = false,
    asesoriaLabelFn,
    soloEditablesRechazados = false,
    revisionHistorial = [],
    keysRechazadasRondaActual = new Set(),
    rondaActual = 1,
    comentariosAnteriores = [],
    historialAnterior = [],
  }) => {
    const [descExpanded, setDescExpanded] = useState(false);
    const isExpanded = expandAll || descExpanded;
    const setCampo = useEvaluacionStore((s) => s.setCampo);
    const marcarRevertido = useEvaluacionStore((s) => s.marcarRevertido);
    const cal: CalificacionType = useEvaluacionStore(
      (s) =>
        s.cambios[req.id]?.calificacion ??
        (s.revertidos[req.id] as CalificacionType | undefined) ??
        s.valoresOriginales[req.id]?.calificacion ??
        'NC'
    );

    const tieneCambioRequisito = useEvaluacionStore((s) => Boolean(s.cambios[req.id]) || s.revertidos[req.id] !== undefined);
    const esBloqueado = req.bloqueado === true && !disabled;
    const esRechazado = req.evaluacionRequisito?.estadoRevision === 'RECHAZADO';
    const esAprobadoAdmin = req.evaluacionRequisito?.estadoRevision === 'APROBADO';

    const reqId = req.evaluacionRequisito?.id;
    const keyRequisito = reqId ? `REQUISITO:${reqId}` : '';
    const fueRechazadoEnRondaActual = keyRequisito ? keysRechazadasRondaActual.has(keyRequisito) : false;

    const editadoTrasRechazo = fueRechazadoEnRondaActual && tieneCambioRequisito;
    // En modo correcciones, lo no rechazado queda bloqueado para edición.
    const bloqueadoPorCorreccion = soloEditablesRechazados && !fueRechazadoEnRondaActual;
    const esInhabilitado = disabled || esBloqueado || bloqueadoPorCorreccion || esAprobadoAdmin;
    const asesoriaLabel =
      esBloqueado && req.bloqueadoPorAsesoriaId
        ? asesoriaLabelFn
          ? asesoriaLabelFn(req.bloqueadoPorAsesoriaId)
          : `#${req.bloqueadoPorAsesoriaId}`
        : '';
    const tooltipBloqueado = esBloqueado
      ? `Bloqueado: cambios pendientes en asesoría ${asesoriaLabel}`
      : esAprobadoAdmin
        ? 'Aprobado por administrador'
        : bloqueadoPorCorreccion
          ? 'Solo puedes editar los elementos rechazados'
          : undefined;

    const futurasModificaciones = req.modificadaEnFuturas || [];
    const tieneFuturas = futurasModificaciones.length > 0;
    const futurasLabels = futurasModificaciones.map((id: number) => (asesoriaLabelFn ? asesoriaLabelFn(id) : `#${id}`));
    const textFuturas = tieneFuturas
      ? futurasModificaciones.length === 1
        ? `Nota: Este valor se modificará en la asesoría posterior ${futurasLabels[0]}. Tenga esto en cuenta al definir este dato.`
        : `Nota: Este valor se modificará en las asesorías posteriores ${futurasLabels.join(', ')}. Tenga esto en cuenta al definir este dato.`
      : '';

    const reqHistorial = useMemo(() => {
      if (!reqId || !revisionHistorial) return [];
      return revisionHistorial.filter((h) => h.elementoTipo === 'REQUISITO' && String(h.elementoId) === String(reqId));
    }, [revisionHistorial, reqId]);

    const comentarioAnterior = useMemo(() => {
      if (!req.id || !comentariosAnteriores) return null;
      return comentariosAnteriores.find((c) => c.elementoTipo === 'REQUISITO' && String(c.elementoId) === String(req.id));
    }, [comentariosAnteriores, req.id]);

    const reqHistorialAnterior = useMemo(() => {
      if (!reqId || !historialAnterior) return [];
      return historialAnterior.filter((h) => h.elementoTipo === 'REQUISITO' && String(h.elementoId) === String(reqId));
    }, [historialAnterior, reqId]);

    return (
      <div className="flex flex-col justify-start">
        <div className="font-bold text-sm text-gray-900">{req.capitulo}</div>
        <div className="flex items-center gap-1">
          <span className="font-bold text-sm text-gray-900">{req.numeral}</span>
          {esBloqueado && (
            <span title={tooltipBloqueado} className="inline-flex items-center text-amber-600 shrink-0">
              <Lock className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
        {esBloqueado && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">{tooltipBloqueado}</div>
        )}
        {tieneFuturas && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1 flex items-start gap-1">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600" />
            <span>{textFuturas}</span>
          </div>
        )}
        {reqHistorialAnterior.length > 0 ? (
          <HistorialAnteriorMatrizCollapse entries={reqHistorialAnterior} />
        ) : comentarioAnterior ? (
          <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1 mt-1 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-blue-600" />
            <span>
              <strong>Comentario anterior:</strong> <span className="italic">"{comentarioAnterior.observacion}"</span>
            </span>
          </div>
        ) : null}
        {fueRechazadoEnRondaActual && reqHistorial.length > 0 ? (
          <RechazoMatrizCollapse entries={reqHistorial} rondaActual={rondaActual} />
        ) : esRechazado ? (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-1 flex items-start gap-1.5">
            <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: '#c0392b' }} />
            <span>
              Rechazado por administrador
              {req.evaluacionRequisito?.observacion ? `: "${req.evaluacionRequisito.observacion}"` : ''}
            </span>
          </div>
        ) : esAprobadoAdmin ? (
          <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 mt-1 flex items-start gap-1.5">
            <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-green-600" />
            <span>Aprobado por administrador</span>
          </div>
        ) : null}
        {editadoTrasRechazo && (
          <div className="text-xs text-[#2d7a4f] bg-[#f0fdf4] border border-[#bbf7d0] rounded px-2 py-1 mt-1 flex items-start gap-1.5">
            <Info className="w-3 h-3 shrink-0 mt-0.5" />
            <span>Editado — guarda los cambios para aplicar la corrección.</span>
          </div>
        )}
        <div className={`text-sm text-gray-700 mt-1 whitespace-pre-wrap ${isExpanded ? '' : 'line-clamp-3'}`}>
          {renderWithBold(req.requisito)}
        </div>
        {!expandAll && (
          <button
            type="button"
            onClick={() => setDescExpanded((v) => !v)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium mt-0.5 self-start"
          >
            {descExpanded ? 'Ver menos' : 'Ver más'}
          </button>
        )}
        {/* Calificación per-requisito */}
        <div className="flex gap-1 mt-2">
          {(['C', 'NC', 'CP', 'NA'] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => !esInhabilitado && setCampo(req.id, 'calificacion', c)}
              disabled={esInhabilitado}
              title={esBloqueado ? tooltipBloqueado : undefined}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors border ${
                esInhabilitado
                  ? cal === c
                    ? 'opacity-50 cursor-not-allowed bg-gray-400 text-white border-gray-400'
                    : 'opacity-40 cursor-not-allowed bg-white text-gray-400 border-gray-200'
                  : cal === c
                    ? c === 'C'
                      ? 'bg-green-600 text-white border-green-600'
                      : c === 'NC'
                        ? 'bg-red-600 text-white border-red-600'
                        : c === 'CP'
                          ? 'bg-yellow-500 text-white border-yellow-500'
                          : 'bg-gray-600 text-white border-gray-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        {/* Diff indicator: shown when current asesoría changed this value vs. prev baseline */}
        {req.calificacionAnterior && cal !== req.calificacionAnterior && (
          <div className="flex items-center gap-1.5 mt-1.5 text-xs">
            <span className="flex items-center gap-1 text-gray-500">
              <span
                className={`px-1.5 py-0.5 rounded font-medium border ${
                  req.calificacionAnterior === 'C'
                    ? 'bg-green-100 text-green-800 border-green-300'
                    : req.calificacionAnterior === 'NC'
                      ? 'bg-red-100 text-red-800 border-red-300'
                      : req.calificacionAnterior === 'CP'
                        ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                        : 'bg-gray-100 text-gray-700 border-gray-300'
                }`}
              >
                {req.calificacionAnterior}
              </span>
              <span>→</span>
              <span
                className={`px-1.5 py-0.5 rounded font-medium border ${
                  cal === 'C'
                    ? 'bg-green-600 text-white border-green-600'
                    : cal === 'NC'
                      ? 'bg-red-600 text-white border-red-600'
                      : cal === 'CP'
                        ? 'bg-yellow-500 text-white border-yellow-500'
                        : 'bg-gray-600 text-white border-gray-600'
                }`}
              >
                {cal}
              </span>
            </span>
            {!esInhabilitado && (
              <button
                type="button"
                onClick={() => marcarRevertido(req.id, req.calificacionAnterior as string)}
                className="flex items-center gap-0.5 text-gray-500 hover:text-gray-800 border border-gray-300 rounded px-1.5 py-0.5 hover:bg-gray-50 transition-colors"
                title="Restaurar valor anterior"
              >
                <RotateCcw className="w-3 h-3" />
                Restaurar
              </button>
            )}
          </div>
        )}
      </div>
    );
  }
);
RequisitosCell.displayName = 'RequisitosCell';

// ========================================
// Formulario de evaluación por terna
// ========================================

const EvaluacionTernaForm = React.memo<{
  evalKey: string;
  evidencias: Evidencia[];
  disabled?: boolean;
  rechazos?: RechazosTernaCampos;
  /** En modo correcciones, solo los campos rechazados quedan editables. */
  soloEditablesRechazados?: boolean;
  /** Ids de evidencias con rechazo vigente (para permitir editar solo esas en modo correcciones). */
  evidenciasRechazadas?: Set<number>;
  evaluacionesTernas?: any[];
  revisionHistorial?: any[];
  keysRechazadasRondaActual?: Set<string>;
  rondaActual?: number;
  comentariosAnteriores?: ComentarioAnterior[];
  historialAnterior?: any[];
}>(
  ({
    evalKey,
    evidencias,
    disabled = false,
    rechazos,
    soloEditablesRechazados = false,
    evidenciasRechazadas,
    evaluacionesTernas = [],
    revisionHistorial = [],
    keysRechazadasRondaActual = new Set(),
    rondaActual = 1,
    comentariosAnteriores = [],
    historialAnterior = [],
  }) => {
    const setCampo = useEvaluacionTernaStore((s) => s.setCampo);
    const { observaciones = '', planMejora = '', fechaImplementacion = '', evidenciaIds = [] } = useTernaEvalValues(evalKey);
    const anterior = useEvaluacionTernaStore((s) => s.valoresAnteriores[evalKey]);

    const [localObs, setLocalObs] = useState(observaciones);
    const [localPlan, setLocalPlan] = useState(planMejora);
    const [localFecha, setLocalFecha] = useState(fechaImplementacion);

    useEffect(() => setLocalObs(observaciones), [observaciones]);
    useEffect(() => setLocalPlan(planMejora), [planMejora]);
    useEffect(() => setLocalFecha(fechaImplementacion), [fechaImplementacion]);

    const evalTerna = useMemo(() => {
      return evaluacionesTernas.find((e) => (e.grupo ? `grupo:${e.grupo}` : `terna:${e.ternaId}`) === evalKey);
    }, [evaluacionesTernas, evalKey]);
    const evalTernaId = evalTerna?.id;

    const fueObsRechazada = evalTernaId ? keysRechazadasRondaActual.has(`OBSERVACIONES:${evalTernaId}`) : false;
    const fuePlanRechazada = evalTernaId ? keysRechazadasRondaActual.has(`PLANMEJORA:${evalTernaId}`) : false;
    const fueFechaRechazada = evalTernaId ? keysRechazadasRondaActual.has(`FECHAIMPLEMENTACION:${evalTernaId}`) : false;

    const obsHistorialAnterior = useMemo(() => {
      if (!evalTernaId || !historialAnterior) return [];
      return historialAnterior.filter((h) => h.elementoTipo === 'OBSERVACIONES' && String(h.elementoId) === String(evalTernaId));
    }, [historialAnterior, evalTernaId]);

    const planHistorialAnterior = useMemo(() => {
      if (!evalTernaId || !historialAnterior) return [];
      return historialAnterior.filter((h) => h.elementoTipo === 'PLANMEJORA' && String(h.elementoId) === String(evalTernaId));
    }, [historialAnterior, evalTernaId]);

    const fechaHistorialAnterior = useMemo(() => {
      if (!evalTernaId || !historialAnterior) return [];
      return historialAnterior.filter((h) => h.elementoTipo === 'FECHAIMPLEMENTACION' && String(h.elementoId) === String(evalTernaId));
    }, [historialAnterior, evalTernaId]);

    const toggleEvidencia = (evId: number) => {
      if (disabled) return;
      const current = evidenciaIds;
      const next = current.includes(evId) ? current.filter((id) => id !== evId) : [...current, evId];
      setCampo(evalKey, 'evidenciaIds', next);
    };

    const obsAnterior = anterior?.observaciones ?? '';
    const planAnterior = anterior?.planMejora ?? '';
    const obsChanged = obsAnterior !== '' && observaciones !== obsAnterior;
    const planChanged = planAnterior !== '' && planMejora !== planAnterior;
    const fechaChanged = (anterior?.fechaImplementacion ?? '') !== '' && fechaImplementacion !== (anterior?.fechaImplementacion ?? '');

    const obsHistorial = useMemo(() => {
      if (!evalTernaId || !revisionHistorial) return [];
      return revisionHistorial.filter((h) => h.elementoTipo === 'OBSERVACIONES' && String(h.elementoId) === String(evalTernaId));
    }, [revisionHistorial, evalTernaId]);

    const planHistorial = useMemo(() => {
      if (!evalTernaId || !revisionHistorial) return [];
      return revisionHistorial.filter((h) => h.elementoTipo === 'PLANMEJORA' && String(h.elementoId) === String(evalTernaId));
    }, [revisionHistorial, evalTernaId]);

    const fechaHistorial = useMemo(() => {
      if (!evalTernaId || !revisionHistorial) return [];
      return revisionHistorial.filter((h) => h.elementoTipo === 'FECHAIMPLEMENTACION' && String(h.elementoId) === String(evalTernaId));
    }, [revisionHistorial, evalTernaId]);

    const isObsApproved = useMemo(() => {
      if (obsHistorial.length === 0) return false;
      return obsHistorial[obsHistorial.length - 1].estado === 'APROBADO';
    }, [obsHistorial]);

    const isPlanApproved = useMemo(() => {
      if (planHistorial.length === 0) return false;
      return planHistorial[planHistorial.length - 1].estado === 'APROBADO';
    }, [planHistorial]);

    const isFechaApproved = useMemo(() => {
      if (fechaHistorial.length === 0) return false;
      return fechaHistorial[fechaHistorial.length - 1].estado === 'APROBADO';
    }, [fechaHistorial]);

    // En modo correcciones, cada campo solo es editable si está rechazado (estado vigente).
    const disabledObs = disabled || (soloEditablesRechazados && !fueObsRechazada) || isObsApproved;
    const disabledPlan = disabled || (soloEditablesRechazados && !fuePlanRechazada) || isPlanApproved;
    const disabledFecha = disabled || (soloEditablesRechazados && !fueFechaRechazada) || isFechaApproved;

    // Feedback: campo rechazado que el asesor ya editó (cambió respecto a su valor anterior).
    const obsEditadaTrasRechazo = fueObsRechazada && obsChanged;
    const planEditadoTrasRechazo = fuePlanRechazada && planChanged;
    const fechaEditadaTrasRechazo = fueFechaRechazada && fechaChanged;

    const obsComentarioAnterior = useMemo(() => {
      if (!evalTernaId || !comentariosAnteriores) return null;
      return comentariosAnteriores.find((c) => c.elementoTipo === 'OBSERVACIONES' && String(c.elementoId) === String(evalTernaId));
    }, [comentariosAnteriores, evalTernaId]);

    const planComentarioAnterior = useMemo(() => {
      if (!evalTernaId || !comentariosAnteriores) return null;
      return comentariosAnteriores.find((c) => c.elementoTipo === 'PLANMEJORA' && String(c.elementoId) === String(evalTernaId));
    }, [comentariosAnteriores, evalTernaId]);

    const fechaComentarioAnterior = useMemo(() => {
      if (!evalTernaId || !comentariosAnteriores) return null;
      return comentariosAnteriores.find((c) => c.elementoTipo === 'FECHAIMPLEMENTACION' && String(c.elementoId) === String(evalTernaId));
    }, [comentariosAnteriores, evalTernaId]);

    const notaEditado = (
      <div className="text-xs text-[#2d7a4f] bg-[#f0fdf4] border border-[#bbf7d0] rounded px-2 py-1 mt-1 flex items-start gap-1.5">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>Editado — guarda los cambios para aplicar la corrección.</span>
      </div>
    );

    return (
      <div className={`w-full h-full ${disabled ? 'opacity-60' : ''}`}>
        <div className="space-y-3">
          {/* Observaciones */}
          <div>
            <Textarea
              id={`obs-terna-${evalKey}`}
              label="Observaciones"
              placeholder="Observaciones..."
              rows={2}
              value={localObs}
              disabled={disabledObs}
              onChange={(e) => !disabledObs && setLocalObs(e.target.value)}
              onBlur={() => !disabledObs && setCampo(evalKey, 'observaciones', localObs)}
            />
            {obsChanged && (
              <div className="mt-1 flex items-start gap-1.5 text-xs text-gray-500">
                <RotateCcw className="w-3 h-3 mt-0.5 shrink-0" />
                <span className="flex-1">
                  <span className="font-medium">Anterior:</span> <span className="italic line-clamp-2">{obsAnterior || '—'}</span>
                </span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => {
                      setLocalObs(obsAnterior);
                      setCampo(evalKey, 'observaciones', obsAnterior);
                    }}
                    className="shrink-0 border border-gray-300 rounded px-1.5 py-0.5 hover:bg-gray-50 transition-colors"
                  >
                    Restaurar
                  </button>
                )}
              </div>
            )}
            {fueObsRechazada && <RechazoMatrizCollapse entries={obsHistorial} rondaActual={rondaActual} />}
            {obsEditadaTrasRechazo && notaEditado}
            {obsHistorialAnterior.length > 0 ? (
              <HistorialAnteriorMatrizCollapse entries={obsHistorialAnterior} />
            ) : obsComentarioAnterior ? (
              <div className="mt-1 flex items-start gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-blue-600" />
                <span>
                  <strong>Comentario anterior:</strong> <span className="italic">"{obsComentarioAnterior.observacion}"</span>
                </span>
              </div>
            ) : null}
            {isObsApproved && (
              <div className="mt-1 flex items-start gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
                <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-green-600" />
                <span>Aprobado por administrador</span>
              </div>
            )}
          </div>

          {/* Plan de Mejora */}
          <div>
            <Textarea
              id={`plan-terna-${evalKey}`}
              label="Plan de Mejora"
              placeholder="Acciones correctivas..."
              rows={2}
              value={localPlan}
              disabled={disabledPlan}
              onChange={(e) => !disabledPlan && setLocalPlan(e.target.value)}
              onBlur={() => !disabledPlan && setCampo(evalKey, 'planMejora', localPlan)}
            />
            {planChanged && (
              <div className="mt-1 flex items-start gap-1.5 text-xs text-gray-500">
                <RotateCcw className="w-3 h-3 mt-0.5 shrink-0" />
                <span className="flex-1">
                  <span className="font-medium">Anterior:</span> <span className="italic line-clamp-2">{planAnterior || '—'}</span>
                </span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => {
                      setLocalPlan(planAnterior);
                      setCampo(evalKey, 'planMejora', planAnterior);
                    }}
                    className="shrink-0 border border-gray-300 rounded px-1.5 py-0.5 hover:bg-gray-50 transition-colors"
                  >
                    Restaurar
                  </button>
                )}
              </div>
            )}
            {fuePlanRechazada && <RechazoMatrizCollapse entries={planHistorial} rondaActual={rondaActual} />}
            {planEditadoTrasRechazo && notaEditado}
            {planHistorialAnterior.length > 0 ? (
              <HistorialAnteriorMatrizCollapse entries={planHistorialAnterior} />
            ) : planComentarioAnterior ? (
              <div className="mt-1 flex items-start gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-blue-600" />
                <span>
                  <strong>Comentario anterior:</strong> <span className="italic">"{planComentarioAnterior.observacion}"</span>
                </span>
              </div>
            ) : null}
            {isPlanApproved && (
              <div className="mt-1 flex items-start gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
                <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-green-600" />
                <span>Aprobado por administrador</span>
              </div>
            )}
          </div>

          {/* Fecha */}
          <div>
            <Input
              id={`fecha-terna-${evalKey}`}
              type="date"
              label="Fecha Implementación"
              value={localFecha}
              disabled={disabledFecha}
              onChange={(e) => !disabledFecha && setLocalFecha(e.target.value)}
              onBlur={() => !disabledFecha && setCampo(evalKey, 'fechaImplementacion', localFecha)}
            />
            {fueFechaRechazada && <RechazoMatrizCollapse entries={fechaHistorial} rondaActual={rondaActual} />}
            {fechaEditadaTrasRechazo && notaEditado}
            {fechaHistorialAnterior.length > 0 ? (
              <HistorialAnteriorMatrizCollapse entries={fechaHistorialAnterior} />
            ) : fechaComentarioAnterior ? (
              <div className="mt-1 flex items-start gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-blue-600" />
                <span>
                  <strong>Comentario anterior:</strong> <span className="italic">"{fechaComentarioAnterior.observacion}"</span>
                </span>
              </div>
            ) : null}
            {isFechaApproved && (
              <div className="mt-1 flex items-start gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
                <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-green-600" />
                <span>Aprobado por administrador</span>
              </div>
            )}
          </div>

          {/* Evidencias */}
          {(() => {
            const visibleEvidencias = evidencias.filter((ev) => {
              if (ev.estado === 'ELIMINADO') return false;
              // Si la evidencia es rechazada y hay un reemplazo activo en la lista, la ocultamos
              const tieneReemplazoActivo = evidencias.some((r) => r.reemplazaEvidenciaId === ev.evidenciaId && r.estado !== 'ELIMINADO');
              return !tieneReemplazoActivo;
            });

            if (visibleEvidencias.length === 0) return null;

            return (
              <div>
                <div className="text-sm font-medium text-gray-700 mb-1">Evidencias</div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {visibleEvidencias.map((ev) => {
                    // En correcciones solo se pueden tocar evidencias con rechazo en la ronda actual.
                    const fueEvRechazada = keysRechazadasRondaActual.has(`EVIDENCIA:${ev.evidenciaId}`);
                    const evDisabled = disabled || (soloEditablesRechazados && !fueEvRechazada);
                    return (
                      <label
                        key={ev.evidenciaId}
                        className={`flex items-center gap-2 text-sm rounded px-1 ${evDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50'}`}
                        title={
                          evDisabled && soloEditablesRechazados && !disabled
                            ? 'Solo puedes modificar evidencias rechazadas en la ronda de corrección actual'
                            : ev.nombre
                        }
                      >
                        <input
                          type="checkbox"
                          checked={evidenciaIds.includes(ev.evidenciaId)}
                          onChange={() => !evDisabled && toggleEvidencia(ev.evidenciaId)}
                          disabled={evDisabled}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                        />
                        <span className="truncate">{ev.nombre}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  }
);
EvaluacionTernaForm.displayName = 'EvaluacionTernaForm';

/**
 * Agrupa los requisitos en FilaEvaluacion[] basados en las ternas devueltas por el API.
 * Retorna GrupoEvaluacion[].
 */
export function buildGruposEvaluacionDesdeTernas(
  ternas: import('@/services/sysadmin.service').TernaDTO[],
  requisitosSinTerna: string[],
  todosRequisitos: RequisitoConAsesoria[],
  ntcs: CumplimientoNTC[],
  columnasNtc?: ColumnaNtc[]
): GrupoEvaluacion[] {
  // Use columnasNtc (from combinacion) when available, otherwise derive from ntcs
  const columnas: ColumnaNtc[] = columnasNtc ?? ntcs.map((ntc) => ({ ntcId: ntc.ntc.ntcId, nombre: ntc.ntc.nombre, orden: ntc.orden }));

  // Crear mapa de NTC id a orden
  const ntcOrdenMap = new Map<number, number>();
  columnas.forEach((col) => {
    ntcOrdenMap.set(col.ntcId, col.orden);
  });

  const reqMap = new Map(todosRequisitos.map((r) => [r.id, r]));
  const filasPorGrupo = new Map<number, FilaEvaluacion[]>();

  for (const terna of ternas) {
    const celdas: Record<number, RequisitoConAsesoria | null> = {};
    for (const col of columnas) celdas[col.orden] = null;

    // C1 → orden 1, C2 → orden 2, C3 → orden 3
    if (terna.requisitoC1Id) celdas[1] = reqMap.get(terna.requisitoC1Id) || null;
    if (terna.requisitoC2Id) celdas[2] = reqMap.get(terna.requisitoC2Id) || null;
    if (terna.requisitoC3Id) celdas[3] = reqMap.get(terna.requisitoC3Id) || null;

    const fila: FilaEvaluacion = {
      id: terna.id.toString(),
      ternaId: terna.id,
      grupo: terna.grupo,
      celdas,
    };

    if (!filasPorGrupo.has(terna.orden)) {
      filasPorGrupo.set(terna.orden, []);
    }
    filasPorGrupo.get(terna.orden)!.push(fila);
  }

  // Array final de grupos
  const grupos: GrupoEvaluacion[] = Array.from(filasPorGrupo.entries())
    .sort(([ordenA], [ordenB]) => ordenA - ordenB)
    .map(([orden, filas]) => ({
      id: `grupo-${orden}`,
      filas: filas.sort((a, b) => {
        if (a.grupo && !b.grupo) return -1;
        if (!a.grupo && b.grupo) return 1;
        if (a.grupo && b.grupo && a.grupo !== b.grupo) return a.grupo.localeCompare(b.grupo);
        return (a.ternaId ?? Number(a.id)) - (b.ternaId ?? Number(b.id));
      }),
    }));

  return grupos;
}

// Cálculo de spans por fusión de celdas adyacentes
function calcularSpansEval(filas: FilaEvaluacion[], ordenes: number[]) {
  const totalRows = filas.length;
  const spans: Record<number, { startRow: number; span: number; req: RequisitoConAsesoria | null }[]> = {};

  for (const orden of ordenes) {
    spans[orden] = [];
    let i = 0;
    while (i < totalRows) {
      const req = filas[i].celdas[orden] ?? null;
      let j = i + 1;
      while (j < totalRows && req !== null && filas[j].celdas[orden]?.id === req.id) {
        j++;
      }
      spans[orden].push({ startRow: i, span: j - i, req });
      i = j;
    }
  }

  return { spans, totalRows };
}

// Calcular spans para la columna de evaluación por terna (agrupa por grupo o terna individual)
interface EvalTernaSpanItem {
  startRow: number;
  span: number;
  evalKey: string | null; // null for requisitosSinTerna
}

function calcularEvalSpansTerna(filas: FilaEvaluacion[]): EvalTernaSpanItem[] {
  const totalRows = filas.length;
  const spans: EvalTernaSpanItem[] = [];
  let i = 0;

  while (i < totalRows) {
    const fila = filas[i];

    if (fila.ternaId == null) {
      // Requisito sin terna — no terna evaluation
      spans.push({ startRow: i, span: 1, evalKey: null });
      i++;
    } else if (fila.grupo) {
      // Grouped terna — merge consecutive with same grupo
      const evalKey = `grupo:${fila.grupo}`;
      let j = i + 1;
      while (j < totalRows && filas[j].grupo === fila.grupo) {
        j++;
      }
      spans.push({ startRow: i, span: j - i, evalKey });
      i = j;
    } else {
      // Individual terna
      spans.push({ startRow: i, span: 1, evalKey: `terna:${fila.ternaId}` });
      i++;
    }
  }

  return spans;
}

interface MatrizEvaluacionProps {
  grupos: GrupoEvaluacion[];
  evidencias: Evidencia[];
  ntcs: CumplimientoNTC[];
  columnasNtc?: ColumnaNtc[];
  className?: string;
  disabled?: boolean;
  asesoriaLabelFn?: (id: number) => string;
  /** Rechazos por evalKey (`grupo:X` / `terna:Y`) para mostrar inline en cada campo de terna. */
  rechazosPorEvalKey?: Map<string, RechazosTernaCampos>;
  /** En modo correcciones, solo los elementos rechazados quedan editables. */
  soloEditablesRechazados?: boolean;
  /** Ids de evidencias con rechazo vigente. */
  evidenciasRechazadas?: Set<number>;
  evaluacionesTernas?: any[];
  revisionHistorial?: any[];
  keysRechazadasRondaActual?: Set<string>;
  rondaActual?: number;
  comentariosAnteriores?: ComentarioAnterior[];
  historialAnterior?: any[];
}

export const MatrizEvaluacion = React.memo(function MatrizEvaluacion({
  grupos,
  evidencias,
  ntcs,
  columnasNtc,
  className = '',
  disabled = false,
  asesoriaLabelFn,
  rechazosPorEvalKey,
  soloEditablesRechazados = false,
  evidenciasRechazadas,
  evaluacionesTernas = [],
  revisionHistorial = [],
  keysRechazadasRondaActual = new Set(),
  rondaActual = 1,
  comentariosAnteriores = [],
  historialAnterior = [],
}: MatrizEvaluacionProps) {
  const columnas = useMemo(() => {
    if (columnasNtc) return [...columnasNtc].sort((a, b) => a.orden - b.orden);
    return [...ntcs].sort((a, b) => a.orden - b.orden).map((n) => ({ ntcId: n.ntc.ntcId, nombre: n.ntc.nombre, orden: n.orden }));
  }, [columnasNtc, ntcs]);
  const ordenes = useMemo(() => columnas.map((c) => c.orden), [columnas]);
  const [expandAll, setExpandAll] = useState(false);

  const gridCols = columnas.map(() => 'minmax(250px, 1fr)').join(' ') + ' minmax(350px, 1.5fr)';

  // Flatten all filas across all groups into a single list
  const flatFilas = useMemo(() => {
    const rows: { fila: FilaEvaluacion; grupoIdx: number; isLastInGrupo: boolean }[] = [];
    for (let gi = 0; gi < grupos.length; gi++) {
      const grupo = grupos[gi];
      for (let fi = 0; fi < grupo.filas.length; fi++) {
        rows.push({
          fila: grupo.filas[fi],
          grupoIdx: gi,
          isLastInGrupo: fi === grupo.filas.length - 1,
        });
      }
    }
    return rows;
  }, [grupos]);

  // Global spans across all flat rows
  const allFilas = useMemo(() => flatFilas.map((r) => r.fila), [flatFilas]);
  const globalSpans = useMemo(() => {
    const { spans } = calcularSpansEval(allFilas, ordenes);
    return spans;
  }, [allFilas, ordenes]);
  const globalEvalSpans = useMemo(() => calcularEvalSpansTerna(allFilas), [allFilas]);

  const totalRows = flatFilas.length;

  // Helper: compute border-bottom for a span that ends at spanEnd
  const getBorderBottom = (spanEnd: number) => {
    if (spanEnd >= totalRows) return undefined;
    const lastRow = flatFilas[spanEnd - 1];
    if (lastRow?.isLastInGrupo) return '3px solid #9ca3af';
    return '1px solid #e5e7eb';
  };

  return (
    <div className={`flex flex-col min-w-0 ${className}`}>
      <div id="matriz-scroll-container" className="flex-1 min-h-0 min-w-0 overflow-auto w-full border border-gray-300 rounded-lg">
        <div style={{ display: 'table', minWidth: '100%' }}>
          {/* Header */}
          <div
            className="sticky top-0 grid z-20 bg-gray-100 border-b-2 border-gray-300 shadow-sm"
            style={{ gridTemplateColumns: gridCols }}
          >
            {columnas.map((col, idx) => (
              <div key={col.orden} className="p-4 font-semibold text-gray-800 flex items-center justify-between">
                <span>{col.nombre}</span>
                {idx === 0 && (
                  <button
                    type="button"
                    onClick={() => setExpandAll((v) => !v)}
                    className="px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  >
                    {expandAll ? 'Colapsar' : 'Expandir'}
                  </button>
                )}
              </div>
            ))}
            <div className="p-4 font-semibold text-gray-800">Evaluación</div>
          </div>

          {/* Single flat grid for all rows (allows cross-grupo rowspan) */}
          {totalRows > 0 ? (
            <div
              className="grid"
              style={{
                gridTemplateColumns: gridCols,
                gridTemplateRows: `repeat(${totalRows}, auto)`,
              }}
            >
              {/* NTC columns */}
              {ordenes.map((orden, colIdx) =>
                globalSpans[orden].map((item, i) => {
                  const spanEnd = item.startRow + item.span;
                  return (
                    <div
                      key={`ntc${orden}-${i}`}
                      className="p-4 bg-white border-r border-gray-200"
                      style={{
                        gridColumn: colIdx + 1,
                        gridRow: `${item.startRow + 1} / span ${item.span}`,
                        borderBottom: getBorderBottom(spanEnd),
                      }}
                    >
                      {item.req ? (
                        <RequisitosCell
                          req={item.req}
                          expandAll={expandAll}
                          disabled={disabled}
                          asesoriaLabelFn={asesoriaLabelFn}
                          soloEditablesRechazados={soloEditablesRechazados}
                          revisionHistorial={revisionHistorial}
                          keysRechazadasRondaActual={keysRechazadasRondaActual}
                          rondaActual={rondaActual}
                          comentariosAnteriores={comentariosAnteriores}
                          historialAnterior={historialAnterior}
                        />
                      ) : (
                        <div className="text-gray-400 text-sm italic">-</div>
                      )}
                    </div>
                  );
                })
              )}

              {/* Evaluación column */}
              {globalEvalSpans.map((item, idx) => {
                const spanEnd = item.startRow + item.span;
                return (
                  <div
                    key={item.evalKey ? `eval-${item.evalKey}-${item.startRow}` : `eval-empty-${item.startRow}`}
                    className="p-4 bg-white"
                    style={{
                      gridColumn: ordenes.length + 1,
                      gridRow: `${item.startRow + 1} / span ${item.span}`,
                      borderBottom: getBorderBottom(spanEnd),
                    }}
                  >
                    {item.evalKey ? (
                      <EvaluacionTernaForm
                        evalKey={item.evalKey}
                        evidencias={evidencias}
                        disabled={disabled}
                        rechazos={rechazosPorEvalKey?.get(item.evalKey)}
                        soloEditablesRechazados={soloEditablesRechazados}
                        evidenciasRechazadas={evidenciasRechazadas}
                        evaluacionesTernas={evaluacionesTernas}
                        revisionHistorial={revisionHistorial}
                        keysRechazadasRondaActual={keysRechazadasRondaActual}
                        rondaActual={rondaActual}
                        comentariosAnteriores={comentariosAnteriores}
                        historialAnterior={historialAnterior}
                      />
                    ) : (
                      <div className="text-gray-400 text-sm italic">Sin terna</div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400">No hay requisitos para evaluar</div>
          )}
        </div>
      </div>
    </div>
  );
});
